// ©2024 Austin App House. All rights reserved.
import React, { useState, useEffect, useContext } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import Tabs from './Tabs';
import WorkOrderCard from './WorkOrderCard';
import getWorkOrders from '../../utils/api/get/getWorkOrders';
import { sortWOTypes, tabs } from '../../utils/constans';
import getWorkOrderCountByTabs from '../../utils/api/get/getWorkOrderCountByTabs';
import {
  setCurrentWorkOrder,
  fetchApplicantsForWorkOrder,
  publishWorkOrder,
  acceptInvite, confirmWorkOrder,
  exportWorkOrders,
  createWorkOrderTemplateByWorkOrder,
  setFiltersObject,
} from '../../storage/actions/workOrdersActions';
import { toggleModal, toggleSecondModalClose } from '../../storage/actions/modalsActions';
import {
  selectWorkOrder,
  setActiveTab,
  setPage,
} from '../../storage/actions/applicationStateActions';
// eslint-disable-next-line import/no-cycle
import { SocketContext } from '../../pages';
import { setMessagesCount, setMessagesSeen } from '../../storage/actions/messageCountAction';
import EstimatedTimeOverageLabel from '../layout/EstimatedTimeOverageLabel';
import PaginationPage from './Pagination';
import SelectDropDownElement from '../layout/selectDropDownElement';
import BulkElements from '../layout/bulkElements';
import DashboardIcon from './icons/DashboardIcon';
import prepareFilters from '../../utils/prepareFilters';

const DashboardContent = ({
  auth,
  profile,
  activeTab,
  isReload,
  setSort,
  match,
  history,
  search,
  filters,
  sortBy,
  selectedWorkOrders,
  selectWorkOrder,
  sortType,
  setActiveTab,
  toggleModal,
  setCurrentWorkOrder,
  fetchApplicantsForWorkOrder,
  setMessagesCount,
  messagesCount,
  setMessagesSeen,
  publishWorkOrder,
  acceptInvite,
  confirmWorkOrder,
  currentPage,
  perPage,
  setPage,
  setFiltersObject,
  exportWorkOrders,
  createWorkOrderTemplateByWorkOrder,
  toggleSecondModalClose,
}) => {
  const [workOrders, setWorkOrders] = useState([]);
  const [workOrderCountByTabs, setWorkOrderCountByTabs] = useState({});
  const { chatSocket: socket } = useContext(SocketContext) ?? {};
  const [estimatedTimeOverageCount, setEstimatedTimeOverageCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isAscending, setIsAscending] = useState(true);

  const fetchData = async (isMounted) => {
    if (!isMounted()) return;

    if (workOrders.length && selectedWorkOrders.length) {
      selectWorkOrder([]);
    }

    const showGlobalLoader = workOrders.length === 0;
    if (showGlobalLoader) {
      toggleModal(true, 'loader', true);
    }

    const closeGlobalLoader = () => {
      if (showGlobalLoader && isMounted()) {
        toggleSecondModalClose('loader', true);
      }
    };

    const tmpFilters = prepareFilters(filters);
    setIsLoading(true);

    try {
      const countPromise = getWorkOrderCountByTabs(
        search,
        tmpFilters,
        auth?.user?.userType,
        activeTab,
      ).then((response) => {
        if (!isMounted()) return;
        setWorkOrderCountByTabs(response?.payload?.workOrderCountByTabs || {});

        if (response.success) {
          const totalWorkOrders = response?.payload?.workOrderCountByTabs[activeTab];
          const countOfPages = Math.max(Math.ceil(totalWorkOrders / perPage), 1);
          if (countOfPages < currentPage && currentPage !== countOfPages) {
            setPage(countOfPages);
          }
        }
      });

      const ordersPromise = getWorkOrders(
        currentPage,
        perPage,
        activeTab,
        search,
        tmpFilters,
        { [sortBy.sortType]: sortBy.order },
        auth?.user?.userType,
      ).then((result) => {
        if (!isMounted()) return;
        const tmpWorkOrders = result.map((order) => {
          if (order?.clientInfo?.ratings) {
            const { total, count } = order.clientInfo.ratings;
            // eslint-disable-next-line no-param-reassign
            order.clientInfo.ratings = ((total / (count * 3)) * 100).toFixed(2).replace(/\.?0+$/, '');
          }
          return order;
        });
        setWorkOrders(tmpWorkOrders);
      });

      await Promise.all([countPromise, ordersPromise]);
    } finally {
      if (isMounted()) {
        setIsLoading(false);
      }
      closeGlobalLoader();
    }
  };

  const selectAllCheckbox = (workOrders) => {
    if (workOrders.length === selectedWorkOrders.length) {
      return selectWorkOrder([]);
    }
    return selectWorkOrder(workOrders);
  };

  const changeActiveTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      history.push(`/dashboard/${tab}`);
    }
  };

  useEffect(() => {
    const { tab } = match.params;
    let tmpTab = '';
    if (tab) tmpTab = tabs[auth.user.userType].find(({ value }) => value === tab);
    // eslint-disable-next-line no-nested-ternary
    tmpTab = tmpTab ? tmpTab.value : auth.user.userType === 'contractor' ? 'upcoming-work' : 'assigned';
    changeActiveTab(tmpTab);
  }, []);

  useEffect(() => {
    const { tab } = match.params;
    if (tab !== activeTab) return undefined;

    let isMounted = true;
    fetchData(() => isMounted);

    return () => {
      isMounted = false;
    };
  }, [
    activeTab,
    sortBy,
    filters,
    search,
    isReload,
    currentPage,
    perPage,
    profile?.status,
    match.params.tab,
  ]);

  useEffect(() => {
    if (!socket) return undefined;

    let isMounted = true;
    const workOrderIds = workOrders.map((el) => String(el._id));

    const requestMessageCounts = () => {
      if (!isMounted) return;
      socket.emit('receiveUnReadMessages', { workOrders: workOrderIds });
    };

    const onUnReadMessages = (data) => {
      if (!isMounted || !Array.isArray(data)) return;
      setMessagesCount(data.reduce((acc, el) => {
        const workOrderId = String(el.workOrderId ?? el.workOrder ?? '');
        if (!workOrderId) return acc;
        acc[workOrderId] = {
          unread: el.count ?? el.unread ?? 0,
          all: el.allCount ?? el.all ?? 0,
        };
        return acc;
      }, {}));
    };

    const onNewMessage = () => requestMessageCounts();
    const onMarkAsSeen = (workOrderId) => {
      if (!isMounted) return;
      setMessagesSeen({ workOrderId: String(workOrderId) });
    };

    socket.on('newMessage', onNewMessage);
    socket.on('unReadMessages', onUnReadMessages);
    socket.on('markAsSeen', onMarkAsSeen);
    socket.on('connect', requestMessageCounts);

    requestMessageCounts();

    return () => {
      isMounted = false;
      socket.off('newMessage', onNewMessage);
      socket.off('unReadMessages', onUnReadMessages);
      socket.off('markAsSeen', onMarkAsSeen);
      socket.off('connect', requestMessageCounts);
    };
  }, [socket, workOrders, setMessagesCount, setMessagesSeen]);

  useEffect(() => {
    if (!selectedWorkOrders.length) {
      setEstimatedTimeOverageCount((prev) => (prev ? 0 : prev));
    } else if (activeTab === 'completed') {
      const overageCount = workOrders.filter(workOrder => selectedWorkOrders
        .includes(workOrder._id)
        && workOrder.basicType === 'Hour'
        && (
          workOrder.approvedHoursQty
          > (
            workOrder.quantity
            + (workOrder.variableType === 'Hour' ? workOrder.maxQuantity : 0)
          )
        )).length;
      setEstimatedTimeOverageCount((prev) => (prev === overageCount ? prev : overageCount));
    }
  }, [selectedWorkOrders.length, activeTab, workOrders]);

  const showBulkSelect = auth.user.userType !== 'contractor'
    || activeTab === 'upcoming-work'
    || activeTab === 'completed'
    || activeTab === 'approved'
    || activeTab === 'paid';

  return (
    <>
      <div className="dashboard-work-area">
        <div className="dashboard-tabs-wrap">
          <div className="dashboard-tabs" role="tablist">
            <Tabs
              tabs={tabs[auth.user.userType]}
              activeTab={activeTab}
              handler={changeActiveTab}
              isCounting={isLoading}
              workOrderCountByTabs={workOrderCountByTabs}
            />
          </div>
          <div className="dashboard-filters-icons" role="toolbar" aria-label="Quick filters">
            <BulkElements userType={auth.user.userType} workOrders={workOrders} />
          </div>
        </div>

        <div className="dashboard-sort-row">
          <div className="sort-controls">
            {showBulkSelect ? (
              <div className="dashboard-bulk-select">
                <SelectDropDownElement
                  isCurrentPageSelected={
                    (workOrders.length > 0)
                    && (workOrders.length === selectedWorkOrders.length)
                  }
                  selectCurrentPage={
                    () => selectAllCheckbox(workOrders.map(el => el._id))
                  }
                />
              </div>
            ) : null}
            <span className="sort-label">Sort by:</span>
            <label className="sort_select">
              <select
                name="sort_by"
                className="sort-value dashboard-input dashboard-input--sort"
                aria-label="sort by"
                onChange={({ target }) => setSort('sortType', target.value)}
                value={sortType}
              >
                {sortWOTypes[auth.user.userType].map(option => (
                  <option key={option.value} value={option.value}>
                    {option.title}
                  </option>
                ))}
              </select>
            </label>
            <label className="sort_type">
              <input
                type="checkbox"
                name="ascending"
                checked={isAscending}
                onChange={({ target }) => {
                  setIsAscending(target.checked);
                  setSort('order', target.checked ? 1 : -1);
                }}
              />
              <span className="sort-direction">
                {isAscending ? 'Ascending' : 'Descending'}
              </span>
            </label>
            {auth.user.userType !== 'contractor'
              && activeTab === 'completed'
              && estimatedTimeOverageCount ? (
                <EstimatedTimeOverageLabel
                  text="Selected WO's include Time Overage!"
                  cardClassName="dashboard-overage-hint"
                />
              ) : null}
          </div>
          <div className="filter-controls">
            <button
              className="filter-btn"
              onClick={() => toggleModal(true, 'filtersModal')}
              aria-label="filter"
              type="button"
            >
              <DashboardIcon name="filter" size={16} strokeWidth="2" />
              Filter <span className="filter-count">({Object.keys(filters).length || 0})</span>
            </button>
            <button
              className="filter-btn filter-btn--reset"
              aria-label="reset"
              type="button"
              onClick={() => setFiltersObject({})}
            >
              <DashboardIcon name="clear" size={16} strokeWidth="2" />
              Reset filter
            </button>
          </div>
        </div>

        <div className="work-orders-list">
          {
            workOrders.length
              ? workOrders.map(el => (
                <WorkOrderCard
                  key={el._id}
                  workOrder={el}
                  activeTab={activeTab}
                  selectedWorkOrders={selectedWorkOrders}
                  userType={auth.user.userType}
                  setCurrentWorkOrder={setCurrentWorkOrder}
                  fetchApplicantsForWorkOrder={fetchApplicantsForWorkOrder}
                  toggleModal={toggleModal}
                  messagesCount={messagesCount}
                  selectWorkOrder={selectWorkOrder}
                  publishWorkOrder={publishWorkOrder}
                  acceptInvite={acceptInvite}
                  confirmWorkOrder={confirmWorkOrder}
                  createWorkOrderTemplateByWorkOrder={createWorkOrderTemplateByWorkOrder}
                />
              ))
              : null
          }
        </div>

        <div className="dashboard-pagination">
          <PaginationPage workOrderCountByTabs={workOrderCountByTabs} />
        </div>
      </div>
    </>
  );
};

const mapDispatchToProps = {
  setCurrentWorkOrder,
  fetchApplicantsForWorkOrder,
  toggleModal,
  setActiveTab,
  selectWorkOrder,
  setMessagesCount,
  setMessagesSeen,
  publishWorkOrder,
  acceptInvite,
  confirmWorkOrder,
  setPage,
  exportWorkOrders,
  createWorkOrderTemplateByWorkOrder,
  toggleSecondModalClose,
  setFiltersObject,
};

const mapStateToProps = state => ({
  auth: state.auth,
  messagesCount: state.messagesCount,
  ...state.applicationState,
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(DashboardContent));
